import {
  AlertsManagementFilterBaseColumns,
  AlertsManagementFilterModel,
  useAlertsManagementListFilter,
  useSetAlertsManagementListFilter,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ScrollView, View } from 'react-native';
import { array, string, object as yupObject } from 'yup';
import { FormContext } from '../forms';
import { DropdownFooter } from '../modals/dropdownFooter';
import { ColumnSelection } from './columnSelection';
import { FilterType } from './filterButton';
import { filterStyles as styles } from './filterStyle';

interface AlertsManagementFilterProps {
  closeFilter: () => void;
  type: FilterType;
}

export const AlertsManagementFilter = ({
  closeFilter,
  type,
}: AlertsManagementFilterProps) => {
  const { data: alertsManagementFilter } = useAlertsManagementListFilter();

  const { mutateAsync: setAlertsManagementListFilter } = useSetAlertsManagementListFilter();

  const formSchema = yupObject({
    ...{ keyword: string().optional().nullable() },
    columns: array().of(string().optional().nullable()).optional().nullable(),
  });

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      columns: alertsManagementFilter?.columns,
      keyword: alertsManagementFilter?.keyword || '',
    },
  });
  const {
    handleSubmit,
    reset,
    formState: { isDirty },
    getValues,
    watch,
  } = formMethods;
  const selectedColumns = watch('columns');

  const isColumnsFilter = type === FilterType.ALERTSMANAGEMENTCOLUMNS;

  const resetForm = useCallback(() => {
    reset({
      ...selectedColumns,
      ...(isColumnsFilter && { columns: new AlertsManagementFilterModel().columns }),
    });
  }, [reset, isColumnsFilter, selectedColumns]);

  const onReset = async () => {
    const updatedFields = {
      ...(isColumnsFilter && { columns: new AlertsManagementFilterModel().columns }),
    };

    resetForm();

    const mergedFilters = { ...alertsManagementFilter, ...updatedFields };
    await applyFilter(new AlertsManagementFilterModel(mergedFilters));
  };

  const onSubmit = async (data: any) => {
    if (!data?.keyword.length) {
      delete data.keyword;
    }
    await applyFilter(new AlertsManagementFilterModel(data));
  };

  const applyFilter = async (filterModel: any) => {
    await Promise.all([setAlertsManagementListFilter(filterModel)]);

    closeFilter();
  };

  const canReset = () => {
    return !Object.values(getValues()).every((value) => value === '');
  };

  useEffect(() => {
    if (!alertsManagementFilter) {
      resetForm();
    }
  }, [alertsManagementFilter, resetForm]);

  const renderStandardData = () => {
    return (
      <ColumnSelection
        baseColumns={AlertsManagementFilterBaseColumns}
        i18nPrefix="alertsManagement.filter"
      ></ColumnSelection>
    );
  };

  return (
    <FormContext.Provider value={formMethods}>
      <View style={styles.container}>
        <View style={[styles.scrollViewContainer, { height: 416 }]}>
          <ScrollView
            contentContainerStyle={styles.scrollViewContent}
            scrollEnabled={true}
            showsVerticalScrollIndicator={true}
          >
            {isColumnsFilter && renderStandardData()}
          </ScrollView>
        </View>

        <DropdownFooter
          isDirty={isDirty}
          onSubmit={handleSubmit(onSubmit)}
          canReset={canReset}
          onReset={onReset}
        />
      </View>
    </FormContext.Provider>
  );
};
